import React from 'react';
import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { frontendDb as db } from '../lib/frontend-db';
import type { PosterItem } from '../lib/frontend-db';

const PostersSection = () => {
  const [posters, setPosters] = useState<PosterItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionTitle, setSectionTitle] = useState('Aktuelles Konzert');

  useEffect(() => {
    loadPosters();
    loadSettings();
  }, []);

  const loadPosters = async () => {
    try {
      const posterItems = await db.getPosters();
      setPosters(posterItems);
    } catch (error) {
      console.error('Error loading posters:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settings = await db.getSettings();
      if (settings) {
        setSectionTitle((settings as any).posters_title || 'Aktuelles Konzert');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  if (loading) {
    return (
      <section id="posters" className="py-16 dark:bg-slate-800 transition-colors">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">{sectionTitle}</h2>
          <div className="flex justify-center gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-[360px] rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden animate-pulse" style={{ backgroundColor: 'var(--bg-primary)' }}>
                <div className="aspect-[2/3] bg-gray-200" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (posters.length === 0) {
    return null;
  }

  const itemCount = posters.length;
  const isSingle = itemCount === 1;

  return (
    <section id="posters" className="py-16 dark:bg-slate-800 transition-colors">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-[var(--primary-color)] mb-8 text-center">Aktuelles Konzert</h2>
        <div className={
          isSingle
            ? "flex justify-center"
            : itemCount === 2
              ? "flex justify-center gap-8"
              : itemCount === 3
                ? "flex justify-center gap-8"
                : "flex flex-wrap justify-center gap-8"
        }>
          {posters.map((poster) => (
            <a
              key={poster.id}
              href={poster.url}
              target="_blank"
              rel="noopener noreferrer"
              className={
                (isSingle ? "max-w-[360px] w-full" : "w-[360px]") +
                " rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-600 group"
              }
              style={{ backgroundColor: 'var(--bg-primary)' }}
            >
              {poster.preview_image && (
                <div className="relative aspect-[2/3]">
                  <img
                    src={poster.preview_image}
                    alt={poster.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-semibold text-[var(--primary-color)]">
                    {poster.title}
                  </h3>
                  <ExternalLink className="text-[var(--primary-color)] opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                </div>
                <p className="mb-4" style={{ color: 'var(--text-primary)' }}>
                  {poster.description.length > 150
                    ? `${poster.description.substring(0, 150)}...`
                    : poster.description}
                </p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {new Date(poster.created_at).toLocaleDateString('de-DE')}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PostersSection;

